import { useMemo, type ReactNode } from "react";
import { usePasswordStrength, type PasswordStrength } from "@/features/auth/usePasswordStrength";
import { usePasswordRequirements } from "@/features/auth/usePasswordRequirements";
import type { PasswordStrengthValidation } from "@/features/auth/passwordStrengthValidation";
import type { PasswordRequirement } from "@/primitives/PasswordRequirements";

// Strength level (from `usePasswordStrength`) → i18n message code.
const STRENGTH_LEVEL_MESSAGE: Record<string, string> = {
	"very-weak": "passwordStrengthResult.veryWeak",
	weak: "passwordStrengthResult.weak",
	medium: "passwordStrengthResult.medium",
	strong: "passwordStrengthResult.strong",
	"very-strong": "passwordStrengthResult.veryStrong",
};

export interface PasswordFeedback {
	/** Ready for `<PasswordStrengthMeter value=… max=… level=… label=… />`. */
	strength: PasswordStrength;
	/** Ready for `<PasswordRequirements requirements=… />`; empty when no policy applies. */
	requirements: PasswordRequirement[];
}

/**
 * timr-localised password feedback: turns a password (+ optional client policy) into the
 * props the `PasswordStrengthMeter` and `PasswordRequirements` primitives need, with every
 * label resolved from the global `messages`.
 *
 * The underlying `usePasswordStrength` / `usePasswordRequirements` stay i18n-agnostic — this
 * is the single place the timr message codes live, so every password screen (change password,
 * reset, invitation, …) wires the labels identically instead of repeating them.
 */
export function usePasswordFeedback(
	password: string,
	validation?: PasswordStrengthValidation,
): PasswordFeedback {
	const strengthLabels = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(STRENGTH_LEVEL_MESSAGE).map(([level, code]) => [level, messages[code]]),
			),
		[],
	);

	const requirementLabels = useMemo<Record<string, ReactNode>>(
		() => ({
			minimumLength: messages.withArguments(
				"user.settings.passwordRules.minimumCharacters",
				validation?.minimumLength?.minimumLength ?? 0,
			),
			lowercase: messages["user.settings.passwordRules.requireLowercase"],
			uppercase: messages["user.settings.passwordRules.requireUppercase"],
			special: messages["user.settings.passwordRules.requireSymbol"],
			digit: messages["user.settings.passwordRules.requireDigit"],
		}),
		[validation],
	);

	const strength = usePasswordStrength(password, {
		labels: strengthLabels,
		emptyLabel: messages["passwordStrength.label"],
	});
	const requirements = usePasswordRequirements(password, validation, { labels: requirementLabels });

	return { strength, requirements };
}
